/* EIM profile widget — shows the signed-in user and which dashboards they
 * can open (with links + what each one does).
 *
 * Canonical copy lives in the eim-auth repo; each dashboard carries its own
 * copy next to its other static assets (same distribution model as
 * eim-theme.css).
 *
 * Dual-use:
 *   - <script src="/eim-profile.js"></script> → window.EIMProfile
 *   - import './eimProfile.js' in a React/Vite/CRA app → window.EIMProfile
 *
 * Usage:
 *   EIMProfile.mount(el, {
 *     apiBase: '',                  // '' same-origin; or 'https://battery.eim.digital'
 *     user: null,                   // optional preloaded {name,email,admin,is_viewer}
 *     currentPortal: 'swapdash',    // marks "you are here" in the list
 *     onLogout: null,               // optional override; default POSTs /api/auth/logout + reload
 *     quiet: false,                 // true → render nothing when not signed in
 *     compact: false,               // true → avatar-only chip (tight headers/sidebars)
 *   })
 *
 * Data comes from eim-auth's /api/auth/me and /api/auth/my-portals.
 */
(function (global) {
  var STYLE_ID = 'eim-profile-style';
  var CSS = [
    '.eimp-root{position:relative;display:inline-flex;font-family:inherit;font-size:14px;line-height:1.4}',
    '.eimp-chip{display:inline-flex;align-items:center;gap:8px;padding:4px 10px 4px 4px;border:1px solid var(--eimp-border);',
    ' background:var(--eimp-surface);color:var(--eimp-text);border-radius:999px;cursor:pointer;font:inherit;max-width:230px}',
    '.eimp-chip:hover{border-color:var(--eimp-teal)}',
    '.eimp-avatar{width:26px;height:26px;border-radius:50%;background:var(--eimp-teal);color:#fff;display:inline-flex;',
    ' align-items:center;justify-content:center;font-size:11px;font-weight:700;letter-spacing:.02em;flex:none}',
    '.eimp-chip-name{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:600}',
    '.eimp-caret{flex:none;opacity:.55;font-size:10px}',
    '.eimp-panel{position:absolute;top:calc(100% + 8px);right:0;z-index:99999;width:330px;max-width:92vw;',
    ' background:var(--eimp-surface);color:var(--eimp-text);border:1px solid var(--eimp-border);border-radius:10px;',
    ' box-shadow:0 12px 32px rgba(0,0,0,.18);overflow:hidden;text-align:left}',
    '.eimp-head{display:flex;gap:12px;align-items:center;padding:14px 16px;border-bottom:1px solid var(--eimp-border)}',
    '.eimp-head .eimp-avatar{width:40px;height:40px;font-size:15px}',
    '.eimp-id{min-width:0}',
    '.eimp-name{font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}',
    '.eimp-email{font-size:12px;color:var(--eimp-muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}',
    '.eimp-role{display:inline-block;margin-top:4px;padding:1px 8px;border-radius:999px;font-size:10.5px;font-weight:700;',
    ' letter-spacing:.04em;text-transform:uppercase;background:var(--eimp-teal-soft);color:var(--eimp-teal)}',
    '.eimp-role.eimp-admin{background:rgba(216,119,41,.14);color:#d87729}',
    '.eimp-sect{padding:10px 16px 4px;font-size:10.5px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--eimp-muted)}',
    '.eimp-list{list-style:none;margin:0;padding:4px 8px 8px;max-height:46vh;overflow:auto}',
    '.eimp-item a{display:block;padding:8px;border-radius:8px;text-decoration:none;color:inherit}',
    '.eimp-item a:hover{background:var(--eimp-hover)}',
    '.eimp-item-label{font-weight:600;font-size:13.5px;display:flex;align-items:center;gap:8px}',
    '.eimp-item-label .eimp-ext{margin-left:auto;opacity:.4;font-size:11px}',
    '.eimp-here{font-size:9.5px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:var(--eimp-teal);',
    ' border:1px solid var(--eimp-teal);border-radius:999px;padding:0 6px}',
    '.eimp-item-desc{font-size:11.5px;color:var(--eimp-muted);margin-top:2px}',
    '.eimp-empty{padding:10px 16px 14px;font-size:12.5px;color:var(--eimp-muted)}',
    '.eimp-foot{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:10px 16px;border-top:1px solid var(--eimp-border)}',
    '.eimp-count{font-size:11.5px;color:var(--eimp-muted)}',
    '.eimp-logout{border:1px solid var(--eimp-border);background:transparent;color:var(--eimp-text);font:inherit;',
    ' font-size:12.5px;font-weight:600;padding:6px 12px;border-radius:8px;cursor:pointer}',
    '.eimp-logout:hover{border-color:#c0392b;color:#c0392b}',
    '.eimp-root{--eimp-surface:#ffffff;--eimp-text:#1c2733;--eimp-muted:#68788a;--eimp-border:#d9e1e8;',
    ' --eimp-hover:rgba(13,148,136,.07);--eimp-teal:#0a7d6e;--eimp-teal-soft:rgba(13,148,136,.12)}',
    '.eimp-root.eimp-dark{--eimp-surface:#1b242e;--eimp-text:#e8eef4;--eimp-muted:#93a4b5;--eimp-border:#324250;',
    ' --eimp-hover:rgba(45,196,179,.10);--eimp-teal:#2dc4b3;--eimp-teal-soft:rgba(45,196,179,.14)}',
  ].join('\n');

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  function initials(nameOrEmail) {
    var s = String(nameOrEmail || '?').trim();
    var at = s.indexOf('@');
    if (at > 0) s = s.slice(0, at).replace(/[._-]+/g, ' ');
    var parts = s.split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  // Detect dark context from the page background so the panel matches the
  // host dashboard without needing its CSS variables.
  function isDarkContext(el) {
    try {
      var node = el;
      while (node && node !== document.documentElement) {
        var bg = getComputedStyle(node).backgroundColor;
        var m = bg && bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
        if (m && (m[4] === undefined || parseFloat(m[4]) > 0.4)) {
          var lum = (0.299 * +m[1] + 0.587 * +m[2] + 0.114 * +m[3]) / 255;
          return lum < 0.45;
        }
        node = node.parentElement;
      }
      var attr = document.documentElement.getAttribute('data-theme');
      if (attr) return attr === 'dark';
      return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch (_) { return false; }
  }

  function fetchJson(url) {
    return fetch(url, { credentials: 'include' }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  function sameHost(url) {
    try { return new URL(url).host === window.location.host; } catch (_) { return false; }
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function mount(container, opts) {
    opts = opts || {};
    if (!container) return null;
    injectStyle();

    var apiBase = (opts.apiBase || '').replace(/\/$/, '');
    var state = { user: opts.user || null, portals: null, open: false, loading: false };

    var root = document.createElement('span');
    root.className = 'eimp-root';
    container.appendChild(root);

    var chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'eimp-chip';
    chip.setAttribute('aria-haspopup', 'true');
    chip.setAttribute('aria-expanded', 'false');
    chip.title = 'Your profile & dashboards';
    root.appendChild(chip);

    var panel = document.createElement('div');
    panel.className = 'eimp-panel';
    panel.style.display = 'none';
    root.appendChild(panel);

    function renderChip() {
      var u = state.user;
      if (!u) { chip.style.display = 'none'; return; }
      chip.style.display = '';
      chip.innerHTML = opts.compact
        ? '<span class="eimp-avatar">' + esc(initials(u.name || u.email)) + '</span>'
        : '<span class="eimp-avatar">' + esc(initials(u.name || u.email)) + '</span>' +
          '<span class="eimp-chip-name">' + esc(u.name || u.email) + '</span>' +
          '<span class="eimp-caret">▾</span>';
      if (opts.compact) chip.style.padding = '3px';
      chip.title = (u.name || u.email) + ' — profile & dashboards';
    }

    function roleLabel(u) {
      if (u.admin) return ['Administrator', 'eimp-admin'];
      if (u.is_viewer || u.viewer_mode) return ['Viewer', ''];
      return ['Member', ''];
    }

    function renderPanel() {
      var u = state.user;
      if (!u) return;
      var role = roleLabel(u);
      var html =
        '<div class="eimp-head">' +
          '<span class="eimp-avatar">' + esc(initials(u.name || u.email)) + '</span>' +
          '<div class="eimp-id">' +
            '<div class="eimp-name">' + esc(u.name || u.email) + '</div>' +
            '<div class="eimp-email">' + esc(u.email || '') + '</div>' +
            '<span class="eimp-role ' + role[1] + '">' + role[0] + '</span>' +
          '</div>' +
        '</div>' +
        '<div class="eimp-sect">Your dashboards</div>';
      if (state.loading) {
        html += '<div class="eimp-empty">Loading…</div>';
      } else if (!state.portals) {
        html += '<div class="eimp-empty">Couldn’t load your dashboard list. Try again later.</div>';
      } else if (!state.portals.length) {
        html += '<div class="eimp-empty">No dashboards granted yet — ask an admin for access.</div>';
      } else {
        html += '<ul class="eimp-list">' + state.portals.map(function (p) {
          var here = p.url && sameHost(p.url) &&
            (!opts.currentPortal || opts.currentPortal === p.id ||
              // hostname shared by several portals (e.g. urjadrishti) — only
              // tag the one matching the current path when we can tell.
              !state.portals.some(function (q) { return q !== p && q.url && sameHost(q.url); }));
          if (opts.currentPortal) here = opts.currentPortal === p.id;
          var target = here ? '' : ' target="_blank" rel="noopener"';
          return '<li class="eimp-item">' +
            '<a href="' + esc(p.url || '#') + '"' + target + '>' +
              '<span class="eimp-item-label">' + esc(p.label) +
                (here ? ' <span class="eimp-here">you are here</span>' : '') +
                (here ? '' : '<span class="eimp-ext">↗</span>') +
              '</span>' +
              (p.description ? '<span class="eimp-item-desc">' + esc(p.description) + '</span>' : '') +
            '</a></li>';
        }).join('') + '</ul>';
      }
      html +=
        '<div class="eimp-foot">' +
          '<span class="eimp-count">' +
            (state.portals ? state.portals.length + ' dashboard' + (state.portals.length === 1 ? '' : 's') : '') +
          '</span>' +
          '<button type="button" class="eimp-logout">Sign out</button>' +
        '</div>';
      panel.innerHTML = html;
      var lo = panel.querySelector('.eimp-logout');
      if (lo) lo.addEventListener('click', function () {
        if (opts.onLogout) return opts.onLogout();
        fetch(apiBase + '/api/auth/logout', { method: 'POST', credentials: 'include' })
          .catch(function () {})
          .finally(function () { window.location.reload(); });
      });
    }

    function loadPortals() {
      if (state.portals || state.loading) return;
      state.loading = true;
      renderPanel();
      fetchJson(apiBase + '/api/auth/my-portals')
        .then(function (d) {
          state.portals = (d && d.portals) || [];
          if (d && d.user && !opts.user) { state.user = Object.assign({}, state.user, d.user); renderChip(); }
        })
        .catch(function () { state.portals = null; })
        .finally(function () { state.loading = false; renderPanel(); });
    }

    function setOpen(open) {
      state.open = open;
      panel.style.display = open ? '' : 'none';
      chip.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        root.classList.toggle('eimp-dark', isDarkContext(container));
        loadPortals();
        renderPanel();
      }
    }

    chip.addEventListener('click', function (e) {
      e.stopPropagation();
      setOpen(!state.open);
    });
    document.addEventListener('click', function (e) {
      if (state.open && !root.contains(e.target)) setOpen(false);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && state.open) setOpen(false);
    });

    if (state.user) {
      renderChip();
    } else {
      // No preloaded user — ask the server. With quiet:true an anonymous
      // visitor sees nothing (used on public/static pages).
      fetchJson(apiBase + '/api/auth/me')
        .then(function (u) { state.user = u; renderChip(); })
        .catch(function () {
          state.user = null;
          if (!opts.quiet) { /* leave hidden either way — chip needs a user */ }
          renderChip();
        });
    }

    return {
      el: root,
      refresh: function () { state.portals = null; if (state.open) loadPortals(); },
      destroy: function () { try { root.remove(); } catch (_) {} },
    };
  }

  var api = { mount: mount, version: 1 };
  global.EIMProfile = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : this);
